<template>
    <form
        class="report-problems__form"
        @submit.prevent>
        <div class="report-problems__form-item">
            <h4
                class="report-problems__form-label">
                Выберите причину обращения
            </h4>
            <div
                :class="['report-problems__form-select', { 'is-error': $v.selectedReportReason.$dirty && $v.selectedReportReason.$invalid }]">
                <STSelect
                    id="report-reason-select"
                    v-model="selectedReportReason"
                    :options="reportReasons"
                    :multiple="false"
                    name="report-reason-select"
                    label="Выберите причину" />

                <span class="select__error">
                    Выберите причину
                </span>
            </div>
        </div>
        <div class="report-problems__form-item">
            <h4
                class="report-problems__form-label">
                Пожалуйста, подробно опишите возникшую проблему
            </h4>
            <div class="report-problems__form-textarea">
                <textarea
                    id="problem-description"
                    v-model.trim="problemDescription"
                    :class="['textarea', {'is-error': $v.problemDescription.$dirty && $v.problemDescription.$invalid}]"
                    name="problem-description"
                    placeholder="Открытый комментарий"
                    @input="$v.problemDescription.$touch()"
                    @blur="$v.problemDescription.$touch()"
                    @focus="$v.problemDescription.$touch()" />
                <span class="textarea__error">Заполните поле</span>
            </div>
        </div>

        <div class="report-problems__form-item">
            <div class="report-problems__form-attach">
                <STDragAndDrop
                    ref="problemScreenshot"
                    title="Приложите скриншот ошибки"
                    :limit="1"
                    :max-size="2"
                    :has-error="isFilesError"
                    :error-text="errorFilesText"
                    @updateUploadFiles="addReasonFiles" />
            </div>
        </div>

        <div class="report-problems__form-item">
            <h4
                class="report-problems__form-label">
                Контакты для связи
            </h4>
            <div class="report-problems__form-contacts">
                <div class="report-problems__form-contact">
                    <STInput id="report-problems-person"
                             v-model.trim="name"
                             :modify="{ 'is-error': $v.name.$dirty && $v.name.$invalid }"
                             name="report-problems-person"
                             :error="nameErrorText"
                             label="Ваше имя"
                             @input="$v.name.$touch()"
                             @focus="$v.name.$touch()" />
                </div>
                <div class="report-problems__form-contact">
                    <STInput
                        id="report-problems-phone"
                        ref="reportProblemsPhoneInput"
                        :value="phone"
                        :modify="{ 'is-error': $v.phone.$dirty && $v.phone.$invalid }"
                        name="report-problems-phone"
                        error="Это обязательное поле"
                        label="Ваш номер телефона"
                        @input="onPhoneInput"
                        @focus="onPhoneFocus" />
                </div>
                <div class="report-problems__form-contact">
                    <STInput
                        id="report-problems-email"
                        v-model.trim="email"
                        name="email"
                        :error="emailErrorText"
                        :modify="{ 'is-error': $v.email.$dirty && $v.email.$invalid }"
                        label="E-mail"
                        @input="$v.email.$touch()"
                        @focus="$v.email.$touch()" />
                </div>
            </div>
            <div v-if="agreementText"
                 class="report-problems__form-agreement">
                <STCheckbox id="report-problems-agreement"
                            v-model="loginAgreement"
                            name="report-problems-agreement">
                    <span v-html="agreementText" />
                </STCheckbox>
            </div>
        </div>

        <STBaseButton
            :disabled="isSendDisabled"
            modify="button_size_small report-problems__form-button"
            text="Отправить"
            @click="send" />
    </form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { AUTH_CHECK_FAILED } from '@/store/settings';
import { maxLength, minLength, required } from 'vuelidate/lib/validators';
import Utils from '@/helpers/Utils';
import {formatPhone, handlePhoneFocus, handlePhoneInput, isPhoneComplete} from '@/helpers/phone';


const onlyLetters = value => {
    if (!value) {
        return false;
    }

    if (!/^[A-Za-zА-Яа-яЁё\s-]+$/.test(value)) {
        return false;
    }

    if (value.startsWith('-') || value.endsWith('-')) {
        return false;
    }

    return (value.match(/-/g) || []).length <= 3;
};

export default {
    name: 'ReportProblemsForm',

    filters: {
        phone: val => `${val}`.replace(/^(\d)(\d{3})(\d{3})(\d{2})(\d{2})$/, '+$1 ($2) $3-$4-$5'),
    },

    data() {
        return {
            problemDescription  : '',    // текстовое поле с описанием проблемы
            name                : '',    // имя
            phone               : '',    // телефон
            email               : '',    // email
            selectedReportReason: [],    // выбранная причина
            loginAgreement      : false, // чекбокс солгасия
            isFilesError        : false, // признак ошибки в поле с приложением
            reasonFiles         : [],    // массив файлов со скриншотами
            errorFilesText      : '',    // текст ошибки
            reportReasons       : [
                { id: 1, value: 'ошибка при авторизации или регистрации', text: 'ошибка при авторизации или регистрации' },
                { id: 2, value: 'не приходит смс', text: 'не приходит смс' },
                { id: 3, value: 'не отображаются мои объекты/сделки', text: 'не отображаются мои объекты/сделки' },
                { id: 4, value: 'мои объекты/сделки отображаются не корректно', text: 'мои объекты/сделки отображаются не корректно' },
                { id: 5, value: 'другая ошибка', text: 'другая ошибка' },
            ]
        };
    },

    computed: {
        ...mapGetters({
            agreementText : 'GET_AGREEMENT',
            userDataLoaded: 'GET_IS_USER_DATA_LOADED',
            userData      : 'GET_USER_DATA',
        }),

        nameErrorText() {
            if (!this.name) {
                return 'Это обязательное поле';
            }

            return 'Имя введено неправильно';
        },

        emailErrorText() {
            if (!this.email) {
                return 'Это обязательное поле';
            }

            return 'Адрес электронной почты введён неправильно';
        },
        isSendDisabled() {
            const hasContactErrors = this.$v.name.$invalid
                || this.$v.phone.$invalid
                || this.$v.email.$invalid;

            if (this.agreementText) {
                return hasContactErrors || !this.loginAgreement;
            }

            return hasContactErrors;
        }

    },

    validations: {
        selectedReportReason: {
            required
        },
        problemDescription: {
            required
        },
        phone: {
            required,
            phoneLength: isPhoneComplete
        },
        name: {
            required,
            onlyLetters,
            minLength: minLength(2),
            maxLength: maxLength(40)
        },
        email: {
            required,
            validMail: value => Utils.validMail(value)
        }
    },

    watch: {
        selectedReportReason() {
            this.$v.selectedReportReason.$touch();
        },

        reasonFiles() {
            this.isFilesError = false;
        }
    },

    created() {
        if (!this.agreementText) {
            this.getAgreement();
        }
    },

    mounted() {
        if (this.userDataLoaded) {
            this.applyUserData(this.userData);
        }
    },

    methods: {
        ...mapActions(['sendProblemsReport', 'getAgreementText']),

        applyUserData(data) {
            this.name = `${data.firstName || ''} ${data.surname || ''}`.trim();
            this.phone = formatPhone(data.phone);
            this.email = data.email;
        },

        /**
         * Получает текст политики
         */
        async getAgreement() {
            try {
                await this.getAgreementText();
            } catch (error) {
                if (error && error.code === AUTH_CHECK_FAILED) {
                    return;
                }

                throw error;
            }
        },

        formatPhone(phone) {
            return this.$options.filters.phone(phone);
        },

        /**
         * Проверяет форму с данными
         * @return {boolean} - верно/не верно заполнена форма
         */
        checkUserData() {
            this.$v.$touch();

            let hasErrors = this.$v.$invalid;

            const errorInDragAndDrop = this.$refs.problemScreenshot.getErrors();

            if (errorInDragAndDrop) {
                this.errorFilesText = errorInDragAndDrop ?
                    'Загруженные файлы не соответствуют требованиям.' :
                    'Приложите скриншот ошибки';

                this.isFilesError = true;
            }

            return hasErrors;
        },

        /**
         * Добавляет файлы в доверенности
         * @param files
         */
        addReasonFiles(files) {
            this.reasonFiles = files;
        },

        send() {
            const error = this.checkUserData();
            
            if (!error) {
                const formData = new FormData();

                this.reasonFiles.forEach((file, index) => {
                    formData.append(`files[${index}]`, file);
                });

                formData.append('name', this.name);
                formData.append('phone', this.phone);
                formData.append('email', this.email);
                formData.append('selectedReportReason', this.selectedReportReason);
                formData.append('problemDescription', this.problemDescription);
            
                this.sendProblemsReport(formData)
                    .then((response) => {
                        this.$emit('response', {
                            status: 200,
                            title : response.data?.title ?
                                response.data.title :
                                'Ваша заявка отправлена!',
                            description: response.data?.description ?
                                response.data.description :
                                'Мы разберёмся в ситуации, и свяжемся с вами. Спасибо!'
                        });
                    })
                    .catch((error) => {
                        console.error(error);
                        this.$emit('response', {
                            status: 400,
                            title : error.data?.title ?
                                error.data.title :
                                'Ой, что-то пошло не так.',
                            description: error.data?.description ?
                                error.data.description : 
                                'Мы не смогли отправить ваше обращение. <br>Пожалуйста, попробуйте ещё раз.'
                        });
                    });
            }
        },

        onPhoneFocus() {
            handlePhoneFocus(this, 'phone', 'reportProblemsPhoneInput');
            this.$v.phone.$touch();
        },

        onPhoneInput(value) {
            handlePhoneInput(this, 'phone', 'reportProblemsPhoneInput', value);
            this.$v.phone.$touch();
        }
    }
};
</script>

<style lang="scss">
@import "ReportProblemsForm";
</style>